import {
  BoardBuilderRankProjections,
  BoardBuilderUnconfirmedSelectionModal
} from '@bo-components/boardBuilder';
import {
  Button,
  PrimaryButton,
  SecondaryButton
} from '@library/components/buttons';
import {
  Combobox,
  type ComboboxOption
} from '@library/components/selects/combobox/Combobox';
import { useMemo, useState } from 'react';
import type { AddPlayers } from '@library/types/boardBuilder';
import type { FormattedGameMode } from '@library/types/gameModes';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import { TabFilter } from '@library/components/filters';
import { convertLeagueProjectionAttributesToSelectOptions } from '@library/component-utils/filters';
import { getGameModeComboboxOptions } from '@bo-utils/gameModes';

export interface BoardBuilderProjectionsFiltersProps {
  disableDurations?: boolean;
  durations: Array<LeagueProjectionAttributes>;
  gameModes: Array<FormattedGameMode> | undefined;
  hasSelectionChanged: boolean;
  mainDurationId: string;
  newlySelectedGameModes: Array<FormattedGameMode>;
  newlySelectedPlayersList: AddPlayers;
  newlySelectedStatTypes: Array<string>;
  onCancelSelectChanges: () => void;
  onConfirmSelectChanges: () => void;
  onDurationChange: (durationId: string) => void;
  onGameModesChange: (
    value: Array<ComboboxOption> | null,
    isFullDuration: boolean
  ) => void;
  onRanking: (startRank: number) => void;
  onStatTypesChange: (value: Array<ComboboxOption> | null) => void;
  rankDisabled?: boolean;
  selectedDurationId: string;
  selectedGameModes: Array<FormattedGameMode> | undefined;
  selectedStatTypes: Array<string>;
  statTypes: Array<LeagueProjectionAttributes>;
  unSelectedGameModes: Array<FormattedGameMode>;
  unSelectedPlayersList: AddPlayers;
  unSelectedStatTypes: Array<string>;
}

export const BoardBuilderProjectionsFilters = ({
  disableDurations = false,
  durations,
  gameModes,
  hasSelectionChanged,
  mainDurationId,
  newlySelectedGameModes,
  newlySelectedPlayersList,
  newlySelectedStatTypes,
  rankDisabled = false,
  onCancelSelectChanges,
  onConfirmSelectChanges,
  onDurationChange,
  onGameModesChange,
  onRanking,
  onStatTypesChange,
  selectedDurationId,
  selectedGameModes,
  selectedStatTypes,
  statTypes,
  unSelectedGameModes,
  unSelectedPlayersList,
  unSelectedStatTypes
}: BoardBuilderProjectionsFiltersProps) => {
  const [showModal, setShowModal] = useState(false);

  const sortedDurations = useMemo(() => {
    if (!mainDurationId) {
      return durations;
    }
    const mainDuration = durations.find(
      (duration) => duration.leagueProjectionFilterId === mainDurationId
    );

    if (!mainDuration) {
      return durations;
    }

    const filteredDurations = durations.filter(
      (duration) => duration.leagueProjectionFilterId !== mainDurationId
    );
    return [mainDuration, ...filteredDurations];
    // using league info as depedency instead of durations to avoid
    // "The final argument passed to useMemo changed size between renders." warning, while switching the leagues
  }, [durations, mainDurationId]);

  const durationOptions =
    convertLeagueProjectionAttributesToSelectOptions(sortedDurations);

  const isFullDuration = useMemo(() => {
    const selectedDurationName = durationOptions.find(
      ({ key }) => key === selectedDurationId
    )?.name;

    return selectedDurationName === 'Full';
  }, [durationOptions, selectedDurationId]);

  const statOptions = statTypes.map(
    ({ leagueProjectionFilterId, leagueProjectionFilterName }) => ({
      label: leagueProjectionFilterName,
      value: leagueProjectionFilterId
    })
  );

  const selectedStatTypeOptions = statTypes
    .filter((statType) =>
      selectedStatTypes.includes(statType.leagueProjectionFilterId)
    )
    .map(({ leagueProjectionFilterId, leagueProjectionFilterName }) => ({
      label: leagueProjectionFilterName,
      value: leagueProjectionFilterId
    }));

  const gameModeOptions = useMemo(
    () => getGameModeComboboxOptions(gameModes, isFullDuration),
    [isFullDuration, gameModes]
  );
  const gameModesValues = useMemo(
    () =>
      // Remove Squads game mode for sub-durations
      getGameModeComboboxOptions(selectedGameModes, isFullDuration).filter(
        ({ isDisabled }) => !isDisabled
      ),
    [isFullDuration, selectedGameModes]
  );

  const handleGameModesChange = (values: Array<ComboboxOption> | null) => {
    onGameModesChange(values, isFullDuration);
  };

  return (
    <section>
      <Combobox
        id="StatTypes"
        isMulti
        label="Stat Types"
        value={selectedStatTypeOptions}
        placeholder="Select Stat Types..."
        onChange={onStatTypesChange}
        options={statOptions}
        allowSelectAll
        isClearable
      />
      <Combobox
        id="gameModes"
        isMulti
        label="Game Modes"
        value={gameModesValues}
        placeholder="Select Game Modes..."
        onChange={handleGameModesChange}
        options={gameModeOptions}
        allowSelectAll
        isClearable
      />
      {hasSelectionChanged && (
        <div className="bg-soFresh-130 border-soFresh-140 mt-4 flex h-[72px] w-full items-center justify-end gap-2 rounded border pr-4">
          <Button
            className="text-soClean-100 text-base underline"
            onClick={() => setShowModal(true)}
          >
            Show Changes
          </Button>
          <SecondaryButton
            disabled={false}
            handleOnClick={onCancelSelectChanges}
            title="Cancel"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            className="flex h-10 items-center justify-center p-4"
            disabled={false}
            handleOnClick={onConfirmSelectChanges}
            title="Confirm Changes"
          >
            Confirm Changes
          </PrimaryButton>
        </div>
      )}
      <div className="flex items-center gap-4 pr-4">
        {selectedDurationId && typeof selectedDurationId === 'string' && (
          <TabFilter
            filterClassName="py-4"
            disabled={disableDurations}
            handleSelect={onDurationChange}
            filterName="duration"
            options={durationOptions}
            selectedValue={selectedDurationId}
            testId="duration-tabs"
          />
        )}
        <BoardBuilderRankProjections
          disabled={rankDisabled}
          resetRankValue={false}
          onRanking={onRanking}
        />
      </div>
      {showModal && (
        <BoardBuilderUnconfirmedSelectionModal
          isOpen={showModal}
          newlySelectedGameModesList={newlySelectedGameModes}
          newlySelectedPlayersList={newlySelectedPlayersList}
          newlySelectedStatTypesList={newlySelectedStatTypes}
          onCancelModal={() => setShowModal(false)}
          statTypes={statTypes}
          unSelectedGameModesList={unSelectedGameModes}
          unSelectedPlayersList={unSelectedPlayersList}
          unSelectedStatTypesList={unSelectedStatTypes}
        />
      )}
    </section>
  );
};
